import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from 'react';

import { IdRedactionStatus } from '@/models/common/IdRedactionStatus';
import { OpenFundRequestPersonRole } from '@/models/common/OpenFundRequestPersonRole';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import CustomDialog from '@/components/ui/Dialog/Dialog';

import {
  boxFromDrag,
  boxToPixels,
  growBox,
  isMeaningfulBox,
  markedPages,
  pagesToSend,
  Point,
  RedactionBox,
  RedactionPage,
  rotateBox,
  Rotation,
  Size,
  turnedBy,
} from './redactionGeometry';
import styles from './OpenFundRequestViewPage.module.scss';

interface IdRedactionDocument {
  status: IdRedactionStatus;
  documentName: string;
  pageCount: number;
  suggestedPages: RedactionPage[];
  appliedPages: RedactionPage[];
}

export interface RedactionTarget {
  personRole: OpenFundRequestPersonRole;
  personIndex: number;
  /** What the file is, in the words the page uses for it - e.g. 'צילום תעודת זהות של רכז 2'. */
  title: string;
}

interface RedactIdDocumentDialogProps {
  requestId: number;
  target: RedactionTarget;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Blacking out the issue date on one ID-card copy (HAT-1303).
 *
 * The office marks the date and confirms; the server burns the marks into the pixels, replaces the stored
 * file and destroys the original. There is no undo - which is the point, and why the confirmation asks
 * plainly rather than through a quiet primary button.
 *
 * The page is shown as an image the server rendered, not as the file itself: while a copy is pending there
 * must be no address the office could save it from.
 *
 * A copy often arrives on its side - a photo that lost its EXIF on the way through WhatsApp, a scan fed in
 * sideways - and a date cannot be marked well on a page lying down. So the office can turn the page. The
 * server does the turning, both for what is shown and for what is burned, so the office is always marking
 * the very picture that gets saved; the boxes already drawn are turned with it so they stay on the date.
 */
const RedactIdDocumentDialog = ({ requestId, target, onClose, onSaved }: RedactIdDocumentDialogProps) => {
  const baseUrl = `funds/creation/${requestId}/id-redaction/${target.personRole}/${target.personIndex}`;

  const [page, setPage] = useState(0);
  const [pages, setPages] = useState<Record<number, RedactionBox[]>>({});
  const [rotations, setRotations] = useState<Record<number, Rotation>>({});
  const [drag, setDrag] = useState<{ start: Point; current: Point } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);

  const { data: redaction, isLoading } = useGetAuthApi<IdRedactionDocument>(baseUrl);

  // A copy that was already redacted was saved the way the office turned it, so what the server shows now
  // is already the right way up; asking it to turn that again would lay it back down.
  const isRedacted = redaction?.status === IdRedactionStatus.Redacted;
  const rotation: Rotation = isRedacted ? 0 : rotations[page] ?? 0;

  const { data: pageImage, isLoading: isLoadingPage } = useGetAuthApi<Blob>(
    `${baseUrl}/pages/${page}${rotation ? `?rotation=${rotation}` : ''}`,
    'blob',
  );
  const { mutateAsync: redact } = usePostAuthApi<void, { pages: RedactionPage[] }>(baseUrl);

  // Whatever the server proposed - today nothing, until an OCR-backed detector is configured - is where the
  // office starts. A copy already redacted shows what was burned, and nothing can be added to it.
  useEffect(() => {
    if (!redaction) return;

    const source = isRedacted ? redaction.appliedPages : redaction.suggestedPages;
    setPages(Object.fromEntries((source ?? []).map((entry) => [entry.page, entry.boxes ?? []])));
  }, [redaction, isRedacted]);

  const imageUrl = useMemo(() => (pageImage ? URL.createObjectURL(pageImage) : null), [pageImage]);
  useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl); }, [imageUrl]);

  const boxes = pages[page] ?? [];

  /**
   * The displayed size of the page, kept in state rather than read while rendering: the boxes are drawn
   * from it, and a size read before the browser has laid the picture out - or one that is now stale
   * because the window was resized - puts them in the wrong place.
   */
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = pageRef.current;
    if (!element) return;

    const measure = () => {
      const rect = element.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [imageUrl]);

  const pointIn = (event: { clientX: number; clientY: number }): Point => {
    const rect = pageRef.current?.getBoundingClientRect();
    return { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
  };

  const setBoxes = (next: RedactionBox[]) => setPages((current) => ({ ...current, [page]: next }));

  const onPointerDown = (event: PointerEvent) => {
    if (isRedacted || isSaving) return;
    event.preventDefault();
    pageRef.current?.setPointerCapture(event.pointerId);
    const point = pointIn(event);
    setDrag({ start: point, current: point });
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag) return;
    setDrag({ ...drag, current: pointIn(event) });
  };

  const onPointerUp = () => {
    if (!drag) return;

    const box = boxFromDrag(drag.start, drag.current, size);
    setDrag(null);

    // A drag of three pixels is a click that slipped, not a rectangle - and a box too small to see is one
    // the office would not notice it left behind.
    if (isMeaningfulBox(box)) {
      setBoxes([...boxes, box]);
      setError(null);
    }
  };

  const removeBox = (index: number) => setBoxes(boxes.filter((_, i) => i !== index));

  /**
   * Turns the page a quarter turn, and the boxes already drawn on it with the picture - a mark that stayed
   * where it was on the screen would no longer be on the date.
   */
  const turn = (delta: 90 | -90) => {
    setDrag(null);
    setError(null);
    setRotations((current) => ({ ...current, [page]: turnedBy(current[page] ?? 0, delta) }));
    setPages((current) => ({ ...current, [page]: (current[page] ?? []).map((box) => rotateBox(box, delta)) }));
  };

  /** A little wider than what was drawn: the office is aiming at a date on a photograph. */
  const padBox = (index: number) =>
    setBoxes(boxes.map((box, i) => (i === index ? growBox(box, 0.01) : box)));

  // Whether the office has covered anything. Turning a page is not covering a date, so it is the boxes
  // that enable the button - the same rule the server holds to.
  const marked = useMemo(() => markedPages(pages), [pages]);
  const toSend = useMemo(() => pagesToSend(pages, rotations), [pages, rotations]);

  const save = useCallback(async () => {
    setError(null);
    setIsSaving(true);

    try {
      await redact({ pages: toSend });
      onSaved();
      onClose();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const status = e?.response?.status ?? e?.status;
      const data = e?.response?.data;
      const message = data?.message ?? (typeof data === 'string' ? data : null) ?? e?.message ?? 'שגיאה לא ידועה';
      setError(status ? `שגיאה ${status}: ${message}` : message);
      setIsConfirming(false);
    } finally {
      setIsSaving(false);
    }
  }, [redact, toSend, onSaved, onClose]);

  const dragBox = drag ? boxFromDrag(drag.start, drag.current, size) : null;

  return (
    <CustomDialog open setOpen={(value) => { if (!value) onClose(); }} hideActionButton>
      <div className={styles.redactDialog}>
        <h3 className="heading-h4-semibold mb-1">{isRedacted ? 'צילום תעודת זהות מושחר' : 'השחרת תאריך ההנפקה'}</h3>
        <p className="body-md-regular mb-2">{target.title}</p>

        {!isRedacted && (
          <p className="body-sm-regular mb-3">
            יש לסמן בגרירה את תאריך ההנפקה בצילום. אם הצילום מונח על הצד, אפשר לסובב אותו לפני הסימון.
            באישור, האזורים המסומנים נצרבים בקובץ והמקור נמחק — לא ניתן לבטל את הפעולה.
          </p>
        )}

        {isLoading && <div className="body-sm-regular">טוען את המסמך…</div>}

        {redaction && (
          <>
            {!isRedacted && (
              <div className={styles.redactToolbar}>
                <Button variant="white" onClick={() => turn(90)} disabled={isSaving || !imageUrl}>
                  ↻ סיבוב ימינה
                </Button>
                <Button variant="white" onClick={() => turn(-90)} disabled={isSaving || !imageUrl}>
                  ↺ סיבוב שמאלה
                </Button>
              </div>
            )}

            <div
              ref={pageRef}
              className={isRedacted ? styles.redactPage : `${styles.redactPage} ${styles.redactPageDrawable}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={() => setDrag(null)}
            >
              {imageUrl
                ? <img src={imageUrl} alt={redaction.documentName} className={styles.redactImage} draggable={false} />
                : <div className={styles.redactPlaceholder}>{isLoadingPage ? 'טוען את העמוד…' : 'העמוד אינו זמין'}</div>}

              {boxes.map((box, index) => (
                <div key={index} className={styles.redactBox} style={boxToPixels(box, size)}>
                  {!isRedacted && (
                    <span className={styles.redactBoxActions}>
                      <button
                        type="button"
                        title="הרחבת הסימון"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={() => padBox(index)}
                      >
                        +
                      </button>
                      <button
                        type="button"
                        title="הסרת הסימון"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={() => removeBox(index)}
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              ))}

              {dragBox && <div className={styles.redactBoxDrawing} style={boxToPixels(dragBox, size)} />}
            </div>

            {redaction.pageCount > 1 && (
              <div className={styles.redactPager}>
                <Button variant="white" onClick={() => setPage(page - 1)} disabled={page === 0 || isSaving}>
                  הקודם
                </Button>
                <span className="body-sm-regular">
                  עמוד {page + 1} מתוך {redaction.pageCount}
                </span>
                <Button
                  variant="white"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= redaction.pageCount - 1 || isSaving}
                >
                  הבא
                </Button>
              </div>
            )}
          </>
        )}

        {error && <div className={styles.errorText}>{error}</div>}

        {isConfirming && !isRedacted && (
          <div className={styles.redactConfirm}>
            האזורים המסומנים ייצרבו בקובץ והמקור יימחק לצמיתות
            {Object.values(rotations).some((value) => value !== 0) && ', והצילום יישמר במצב שבו הוא מוצג כעת'}
            . להמשיך?
          </div>
        )}

        <div className={styles.actionsBar}>
          {!isRedacted && !isConfirming && (
            <Button
              variant="primary"
              onClick={() => { setError(null); setIsConfirming(true); }}
              disabled={marked.length === 0 || isSaving}
            >
              השחרה
            </Button>
          )}
          {!isRedacted && isConfirming && (
            <Button variant="primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'מבצע השחרה…' : 'אישור ההשחרה'}
            </Button>
          )}
          <Button variant="white" onClick={isConfirming ? () => setIsConfirming(false) : onClose} disabled={isSaving}>
            {isConfirming ? 'חזרה לסימון' : 'סגירה'}
          </Button>
        </div>
      </div>
    </CustomDialog>
  );
};

export default RedactIdDocumentDialog;
